import { getIconColor } from "../../../Shared/Components/Layout/menu/helpers/getIconColor";
import { MenuItemStatus } from "../../../Shared/Components/Layout/menu/types/MenuItemStatus";

// Texto fixo do topo do tile (SGD-3677 RN4c: "MOD XX"). O número fica no `value` do AStatusTile.
export const PCS_MODULE_LABEL = "MOD";

/**
 * Número do módulo formatado com dois dígitos, a partir da referência do teleobjeto (RN4c).
 * Ex.: 1 -> "01", 15 -> "15". Sem referência -> "--".
 */
export const formatModuleNumber = (reference: number | null): string => {
    if (reference == null) return "--";
    return String(reference).padStart(2, "0");
};

/**
 * Nome do estado extraído do status no padrão "<Nome> - <HEX>" (ex.: "Normalizado - 1BB483" -> "Normalizado").
 * Sem o separador, devolve o próprio status.
 */
const statusName = (status: string): string => {
    if (!status) return "";
    return status.includes(" - ") ? status.split(" - ")[0].trim() : status.trim();
};

// Cores de fundo pareadas ao estado (tokens UI/UX). A borda/texto vem do HEX do próprio status (getIconColor).
const BACKGROUND_BY_STATE: Record<string, string> = {
    Normalizado: "#EDF7ED", // alert/success/background
    Alarmado: "#FDEDED", // alert/error/background
    "Sem dados": "#0000000A", // neutro claro
};

const DEFAULT_BACKGROUND = "#0000000A";

export interface PcsModuleStyle {
    color: string; // borda e texto
    backgroundColor: string; // fundo do tile
}

/**
 * Resolve o par (cor da borda/texto, cor de fundo) de um módulo a partir do status do backend.
 * A cor principal vem do HEX embutido no status (via getIconColor); o fundo, do token pareado ao estado.
 */
export const resolvePcsModuleStyle = (status: string): PcsModuleStyle => {
    const color = getIconColor((status ?? "") as MenuItemStatus);
    const backgroundColor = BACKGROUND_BY_STATE[statusName(status)] ?? DEFAULT_BACKGROUND;
    return { color, backgroundColor };
};

// Itens da legenda (Normalizado / Alarmado / Sem dados). As cores são as mesmas do tile PCS,
// embutidas no status "<Nome> - <HEX>" e resolvidas pelo getIconColor.
export const PCS_LEGEND_ITEMS: Array<{ label: string; status: string }> = [
    { label: "Normalizado", status: "Normalizado - 1BB483" },
    { label: "Alarmado", status: "Alarmado - D32F2F" },
    { label: "Sem dados", status: "Sem dados - 0000003B" },
];

/**
 * Subtítulo dinâmico da seção, derivado da própria lista de módulos (sem chamada extra ao backend).
 * Ex.: 24 módulos -> "24 módulos monitorados | MOD01 - MOD24". Quantidade dinâmica por BESS.
 * Sem referências válidas, devolve apenas a contagem. Vazio -> "".
 */
export const buildPcsSummary = (modules: Array<{ reference: number | null }>): string => {
    const count = modules.length;
    if (count === 0) return "";

    const noun = count === 1 ? "módulo monitorado" : "módulos monitorados";
    const summary = `${count} ${noun}`;

    const references = modules
        .map((module) => module.reference)
        .filter((reference): reference is number => reference != null);
    if (references.length === 0) return summary;

    const min = Math.min(...references);
    const max = Math.max(...references);
    const range =
        min === max
            ? `MOD${formatModuleNumber(min)}`
            : `MOD${formatModuleNumber(min)} - MOD${formatModuleNumber(max)}`;
    return `${summary} | ${range}`;
};
